import { DatePipe, DecimalPipe, NgClass, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { CurrencySymbolComponent } from '../../../../../core/components/currency-symbol/currency-symbol.component';
import { AmountPipe } from '../../../../../core/pipes/amount.pipe';
import { AuthService } from '../../../../auth/auth.service';
import { ApprovalItem, ApprovalsService } from '../../../services/approvals.service';

@Component({
  selector: 'app-approval-queue-list',
  standalone: true,
  imports: [NgClass, DecimalPipe, DatePipe, TitleCasePipe, AmountPipe, MatIconModule, MatProgressSpinnerModule, MatButtonModule, MatTooltipModule, CurrencySymbolComponent],
  templateUrl: './approval-queue-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApprovalQueueListComponent implements OnInit {
  private approvalsService = inject(ApprovalsService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() filters: any = {};
  @Input() maxItems = 5;
  @Input() title = 'Pending Approvals';
  @Input() requiredPermission?: string;

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  get currencyCode(): string {
    return this.auth.profile()?.company?.currency?.currency_code || 'AED';
  }

  approvals: ApprovalItem[] = [];
  totalApprovals = 0;
  loading = false;
  error: string | null = null;

  ngOnInit(): void {
    if (!this.hasPermission) return;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.approvalsService
      .getMyApprovals(this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const data = res.record ?? res;
          const all = Array.isArray(data?.items) ? data.items : [];
          this.totalApprovals = data?.total ?? all.length;
          this.approvals = all.slice(0, this.maxItems);
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.status === 403 ? 'Access restricted' : err.status === 500 ? 'Service unavailable' : 'Unable to load data';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  takeAction(item: ApprovalItem): void {
    const route = this.getActionRoute(item);
    this.router.navigate(route.path, { queryParams: route.queryParams });
  }

  private getActionRoute(item: ApprovalItem): { path: any[]; queryParams: Record<string, any> } {
    switch (item.type) {
      case 'bill':
        return { path: ['/accounting/bills'], queryParams: { billId: item.id } };
      case 'application':
        return { path: ['/eservices/list'], queryParams: { id: item.id } };
      case 'job':
        return { path: ['/procurement/work-orders/detail', item.id], queryParams: {} };
      case 'jv':
        return { path: ['/accounting/journal-vouchers'], queryParams: { id: item.id } };
      case 'credit_note':
        return { path: ['/accounting/credit-notes'], queryParams: { id: item.id } };
      default:
        return { path: ['/accounting/bills'], queryParams: { billId: item.id } };
    }
  }

  isValidDate(value: any): boolean {
    if (!value) return false;
    const d = new Date(value);
    return !isNaN(d.getTime());
  }

  getLicenseExpiryClass(expiry: string | undefined): string {
    if (!expiry) return 'text-gray-400';
    const exp = new Date(expiry);
    const now = new Date();
    const days = (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    if (days < 0) return 'text-red-600 font-semibold';
    if (days <= 30) return 'text-orange-500 font-semibold';
    return 'text-gray-400';
  }

  getTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      bill: 'receipt_long',
      application: 'assignment',
      job: 'construction',
      jv: 'account_balance',
      credit_note: 'receipt',
    };
    return icons[type] ?? 'approval';
  }

  getTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      bill: 'Vendor Bill',
      application: 'Application',
      job: 'Job / Work Order',
      jv: 'Journal Voucher',
      credit_note: 'Credit Note',
    };
    return labels[type] ?? type;
  }
}
